export default angular.module('eventix.common.services.eventixSocket', [])
/**
 * @ngdoc service
 * @name eventix.common.services.eventixSocket
 * @description
 * Handle websocket connection to Eventix NATS
 */
    .factory('EventixSocket', EventixSocket)
    .name;

function EventixSocket($q, $timeout, store, UIMessages, $analytics) {
    let socketer = null;

    const delay = 500;
    const delayLimit = 10;
    let delayCount = 0;
    let delayQueue = $q.resolve();

    const uiMessageDuration = 2500;

    let muted = true;
    let volume = 0.6;

    return {
        initialize: function(newToken) {
            if (socketer) {
                console.error('Eventix Socketer: Initialize: Already initialized, updating token');

                socketer.updateToken(newToken);

                return this;
            }

            if (!SOCKETER_HOST) {
                console.error('Eventix Socketer: Initialize: Can not connect without host');

                return this;
            }

            if (!SOUND_BASE)
                console.error('Eventix Socketer: Initialize: Sound base not configured.');

            let storedMute = store.get('eventix.socketer.muted')

            if (!_.isNil(storedMute))
                setMuted(storedMute);

            socketer = new Socketer(SOCKETER_HOST, newToken);

            return this;
        },

        updateToken: function(newToken) {
            if (_.isNil(socketer)) {
                console.error('Eventix Socketer: UpdateToken: Socketer not initialized.');

                return;
            }

            if (_.isNil(newToken) || !_.isString(newToken) || !newToken.length) {
                console.error('Eventix Socketer: UpdateToken: Token invalid.', newToken);

                return;
            }

            socketer.updateToken(newToken);
        },

        subscribe: function(subject, callback) {
            if (_.isNil(socketer)) {
                console.error('Eventix Socketer: Subscribe: Socketer not initialized.');

                return null;
            }

            return socketer.subscribe(subject, callback);
        },

        unsubscribe: function(subject, key) {
            if (_.isNil(socketer)) {
                console.error('Eventix Socketer: Unsubscribe: Socketer not initialized.');

                return;
            }

            socketer.unsubscribe(subject, key);
        },

        playSound: function(subject) {
            if (_.isNil(socketer)) {
                console.error('Eventix Socketer: PlaySound: Socketer not initialized.');

                return $q.reject();
            }

            if (!socketer.hasSubscription(subject)) {
                console.error('Eventix Socketer: PlaySound: Socketer does not have subscription', subject);

                return $q.reject();
            }

            if (!SOUND_BASE) return $q.reject();

            // If the queue is too big, return a rejection.
            // Eg: 10000 order.payed messages in 10 minutes would result in 2h45m of jingling fun :)
            if (delayCount >= delayLimit) return $q.reject();

            delayCount++;

            let url = SOUND_BASE.replace(/\/?$/, '/') + subject + '.mp3';


            return delayQueue = delayQueue.then(() => {
                let audio = new Audio(url);
                audio.volume = volume;

                return $timeout(() => {
                    delayCount--;

                    if (muted)
                        return $q.resolve();

                    return audio.play().catch(() => $q.resolve());
                }, delay);
            });
        },

        pushUIMessage: function(message) {
            if (!muted) {
                UIMessages.push({
                    message: message,
                    timeout: uiMessageDuration,
                    type: 'success'
                });
            }

            return $q.resolve();
        },

        toggleMute: function() {
            setMuted(!muted);

            UIMessages.push({
                message: 'common.pubsub.' + (muted ? 'disabled' : 'enabled'),
                timeout: uiMessageDuration,
                type: 'success'
            });

            $analytics.eventTrack('turned-sound-'+(muted ? 'off' : 'on'));
        },

        isMuted: function() {
            return muted;
        }
    };

    function setMuted(value) {
        muted = value;

        store.set('eventix.socketer.muted', muted);
    }
}

function Socketer(host, token) {
    let websocket = true;
    let connection = false;

    // backoff variables
    let backOffPosition = 0;
    const HIGHEST_BACKOFF = 55;
    const backOff = [0, 1, 1, 2, 3, 5, 8, 13, 21, 34, HIGHEST_BACKOFF];

    // Current timeout id
    let currentTimeout = -1;

    let currentMessage = null;

    // Callbackmap
    let callbackMap = {};

    // Attempt connection
    disconnectHandle();

    // Private methods
    function handleMessage(event) {
        if (!connection) {
            if (event.data === 'w') {
                connection = true;
                backOffPosition = 0;

                console.debug('Eventix Socket: Connected');

                return;
            }

            return;
        }

        if (_.isNil(currentMessage) && _.has(callbackMap, [event.data])) {
            currentMessage = event.data;
        } else if (_.isNil(currentMessage)) {
            console.debug('Eventix Socketer: Message: Will skip ', event.data);
        } else {
            console.debug('Eventix Socketer: Message: About to handle ', currentMessage);

            let data, callbacks = _.get(callbackMap, [currentMessage], []);

            try {
                data = JSON.parse(event.data);
            } catch(error) {
                data = event.data;
            }

            _.forOwn(callbacks, (callback, key) => {
                console.debug('  Eventix Socketer: Message: Firing callback', currentMessage, key);

                window.setTimeout(callback.call(null, data));
            });

            currentMessage = null;
        }
    }

    function connect() {
        if (!_.isNil(websocket)) return;

        console.debug('Eventix Socketer: Connect: Attempt connecting to ', host + token);

        websocket = new WebSocket(host + token);

        websocket.onmessage = handleMessage;
        websocket.onclose = disconnectHandle;
        websocket.onerror = disconnectHandle;

        // Reset timeout waiter id
        websocket.onopen = function() { currentTimeout = -1; };
    }

    function disconnectHandle() {
        if (_.isNil(websocket)) return;

        connection = false;
        websocket = null;

        // Attempt to reconnect, after backoff
        let currentBackOff = _.get(backOff, backOffPosition++, HIGHEST_BACKOFF);

        console.debug('Eventix Socketer: Disconnect: Attempting reconnect in', currentBackOff, 'seconds');

        currentTimeout = window.setTimeout(connect, currentBackOff * 1000);
    }

    // Public methods
    this.forceReconnect = function() {
        if (currentTimeout < 0)
            return;

        console.debug('Eventix Socketer: ForceReconnect: Forcing reconnection');

        window.clearTimeout(currentTimeout);
        window.setTimeout(connect, 1);
    };

    this.updateToken = function(newToken) {
        console.debug('Eventix Socketer: UpdateToken: Setting new token', newToken);

        token = newToken;

        if (_.isNil(websocket) || !connection) {
            console.debug('Eventix Socketer: UpdateToken: Connection was lost, reconnect', newToken);

            this.forceReconnect();
        } else {
            console.debug('Eventix Socketer: UpdateToken: Updating token', newToken);

            websocket.send(newToken);
        }
    };

    this.subscribe = function(subject, callback) {
        // Generate unique identifier
        let key = new Date().getTime();

        _.set(callbackMap, [subject, key], callback);

        return key;
    };

    this.unsubscribe = function(subject, key) {
        _.unset(callbackMap, [subject, key]);
    };

    this.hasSubscription = function(subject) {
        return _.has(callbackMap, [subject]);
    };
}
